import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatPrice } from '@utils/formatPrice';
import { QuantityCounter } from '@shared/components/inputs/QuantityCounter/QuantityCounter';
import { DiscountBadge } from '@shared/components/display/DiscountBadge/DiscountBadge';
import { SelectableCard } from '@shared/components/SelectableCard';
import type { ParsedAddOn } from '@t/addOn.types';

type AddOnCardProps = {
  addOn: ParsedAddOn;
  isSelected: boolean;
  quantity: number;
  onToggle: () => void;
  onQuantityChange: (qty: number) => void;
};

export const AddOnCard = ({ addOn, isSelected, quantity, onToggle, onQuantityChange }: AddOnCardProps) => {
  const { name, price, description, discountTag, discountMessage, supportsQuantity } = addOn;
  const formattedPrice = formatPrice(price);
  const priceLabel = supportsQuantity ? `${formattedPrice} / month per unit` : `${formattedPrice} / month`;

  return (
    <SelectableCard selectionMode="multi" selected={isSelected} onSelect={onToggle}>
      {(selectable) => (
        <Card variant="addOnCard" selected={isSelected} elevation={0} {...selectable}>
          <Checkbox checked={isSelected} tabIndex={-1} disableRipple />

          <Stack className="AddOnCard-content" gap="10px">
            <Typography variant="title.card">{name}</Typography>

            <Typography variant="price.card" className="AddOnCard-price">
              {priceLabel}
            </Typography>

            {discountTag && (
              <DiscountBadge>
                <Typography variant="discount.tag" className="AddOnCard-price">
                  {discountTag}
                </Typography>
              </DiscountBadge>
            )}

            {discountMessage && (
              <Typography variant="discount.text" className="AddOnCard-message">
                {discountMessage}. Discount applied at checkout
              </Typography>
            )}

            <Typography
              variant="body.md"
              className="AddOnCard-message"
              maxLines={3}
            >
              {description}
            </Typography>

            {supportsQuantity && (
              <Stack gap="5px">
                <Stack direction="row" alignItems="center" gap="10px">
                  <Typography variant="label.fieldSmall" className="AddOnCard-quantityLabel">
                    Quantity:
                  </Typography>
                  <QuantityCounter value={Number(quantity)} min={1} max={10} onChange={onQuantityChange} />
                </Stack>
                <Typography variant="body.sm" className="AddOnCard-message">
                  {`Each quantity adds ${formattedPrice} / month`}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Card>
      )}
    </SelectableCard>
  );
};
